import { CircularProgress, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { PremiseAddress } from "@shared/types/foyer";

interface Props {
  /** Called when the user picks a suggestion. Fields are best-effort from
   *  Google's `address_components` parsing - empty when not returned. */
  onPick: (next: PremiseAddress) => void;
  /** Optional initial input value (e.g. the current address). */
  initialValue?: string;
}

/**
 * Thin Google Places Autocomplete wrapper using the Places library's
 * `Autocomplete` class. The key is baked into the bundle at build time
 * via VITE_GOOGLE_MAPS_API_KEY (domain-restricted in Google Console).
 *
 * If the key is missing, this component degrades to a passive text
 * input that does nothing - the caller's "Search" fallback (Nominatim)
 * still works in PremiseLocationCard.
 */
export default function GooglePlacesAutocomplete({
  onPick,
  initialValue = "",
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState(initialValue);
  const [loading, setLoading] = useState(true);
  const [scriptError, setScriptError] = useState(false);
  const keyConfigured = Boolean(import.meta.env.VITE_GOOGLE_MAPS_API_KEY);

  useEffect(() => {
    if (!keyConfigured) {
      setLoading(false);
      return;
    }
    void loadGoogleMaps()
      .then(() => setLoading(false))
      .catch(() => {
        setScriptError(true);
        setLoading(false);
      });
  }, [keyConfigured]);

  useEffect(() => {
    if (loading || scriptError || !inputRef.current) return;
    if (!keyConfigured) return;
    const google = (window as unknown as { google?: typeof window.google })
      .google;
    if (!google?.maps?.places?.Autocomplete) return;
    const ac = new google.maps.places.Autocomplete(inputRef.current, {
      types: ["geocode"],
      fields: ["geometry.location", "address_components", "formatted_address"],
    });
    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const loc = place.geometry?.location;
      if (!loc) return;
      onPick({
        ...parseAddressComponents(place.address_components ?? []),
        lat: typeof loc.lat === "function" ? loc.lat() : (loc.lat as number),
        lng: typeof loc.lng === "function" ? loc.lng() : (loc.lng as number),
      });
      setValue(place.formatted_address ?? "");
    });
    return () => {
      listener.remove();
      // Clear the .pac-container popups Google leaves behind on unmount.
      document.querySelectorAll(".pac-container").forEach((el) => el.remove());
    };
  }, [loading, scriptError, keyConfigured, onPick]);

  if (!keyConfigured) {
    return null;
  }

  return (
    <TextField
      inputRef={inputRef}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder={
        loading
          ? "Loading Google Places…"
          : scriptError
            ? "Google Places failed to load - try refreshing"
            : "Start typing an address…"
      }
      size="small"
      fullWidth
      disabled={loading || scriptError}
      InputProps={{
        endAdornment: loading ? <CircularProgress size={16} /> : null,
      }}
      inputProps={{ "data-testid": "places-autocomplete" }}
      sx={{
        // Lift the autocomplete dropdown above MUI dialogs (zIndex 1300).
        "& + .pac-container, & ~ .pac-container": { zIndex: 1400 },
      }}
    />
  );
}

/**
 * Lazy-load the Google Maps JavaScript SDK with the Places library.
 * Caches the promise so multiple components sharing the page only
 * trigger one network fetch.
 */
let mapsLoaderPromise: Promise<void> | null = null;
function loadGoogleMaps(): Promise<void> {
  if (mapsLoaderPromise) return mapsLoaderPromise;
  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!key) return Promise.reject(new Error("no_key"));
  // If the script is already on the page (from a different bundle), wait
  // for `google.maps.places` to appear.
  if (
    (window as unknown as { google?: typeof window.google }).google?.maps
      ?.places
  ) {
    return Promise.resolve();
  }
  mapsLoaderPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(
      'script[data-foyer-maps-loader="1"]'
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("script_error"))
      );
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      key
    )}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.dataset.foyerMapsLoader = "1";
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => reject(new Error("script_error")));
    document.head.appendChild(script);
  });
  return mapsLoaderPromise;
}

/**
 * Translate Google's `address_components` array into our flat
 * PremiseAddress shape. Google's component types are stable enough
 * that we can look up by `types[0]` without too much guesswork.
 */
function parseAddressComponents(
  components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>
): Omit<PremiseAddress, "lat" | "lng"> {
  const get = (type: string, short = false): string | undefined => {
    const c = components.find((x) => x.types.includes(type));
    if (!c) return undefined;
    return short ? c.short_name : c.long_name;
  };
  const street = [get("street_number"), get("route")].filter(Boolean).join(" ");
  return {
    streetAddress: street || undefined,
    postalCode: get("postal_code"),
    city:
      get("postal_town") ??
      get("locality") ??
      get("administrative_area_level_2"),
    country: get("country", true)?.toUpperCase(),
    region: get("administrative_area_level_1"),
    // Google doesn't return IANA timezones via Places - we'd need the
    // separate Time Zone API. Leaving timezone alone.
  };
}

// Module augmentation for the Google Maps namespace. We only use a
// tiny slice; no need to bring in @types/google.maps.
declare global {
  interface Window {
    google?: {
      maps: {
        places: {
          Autocomplete: new (
            input: HTMLInputElement,
            opts?: {
              types?: string[];
              fields?: string[];
            }
          ) => GoogleAutocomplete;
        };
      };
    };
  }
}

interface GoogleAutocomplete {
  addListener(event: string, cb: () => void): { remove(): void };
  getPlace(): {
    geometry?: {
      location?: { lat: number | (() => number); lng: number | (() => number) };
    };
    address_components?: Array<{
      long_name: string;
      short_name: string;
      types: string[];
    }>;
    formatted_address?: string;
  };
}
